import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { AttributeFilter, DashboardPresetFilters } from '@/entities/dashboards';
import { type DashboardPresetItem, useDashboardPresets } from '@/entities/settings';
import type { DashboardFiltersState } from '../ui/QualityFilters';
import { getDefaultPanelFilters } from './dashboardFilterDefaults';

/**
 * Задержка записи фильтров серверного дашборда тем, кто его правит: запись уходит на сервер, а поля
 * панели (период, текстовый атрибутный фильтр) отдают значение на каждое нажатие.
 */
const SERVER_WRITE_DELAY_MS = 500;

/** Смена фильтров серверного дашборда, ещё не записанная на сервер. */
type PendingFilters = {
  presetId: string;
  filters: DashboardPresetFilters;
};

/** Фильтры стандартного дашборда, сохраняемые за пользователем, и их обработчики. */
type StandardPanelFilters = {
  filters: DashboardFiltersState;
  activeAttributeFilters: AttributeFilter[];
  handleChangeFilters: (next: DashboardFiltersState) => void;
  handleChangeAttributeFilters: (next: AttributeFilter[]) => void;
  handleResetFilters: () => void;
};

/**
 * Фильтры панели страницы дашбордов. Пока применён пользовательский дашборд, период и атрибутные
 * фильтры — его собственные: панель показывает и меняет их, а фильтры стандартного дашборда,
 * сохранённые за пользователем, не трогает. Режима тепловой карты в панели пользовательского
 * дашборда нет: в фильтрах страницы остаётся режим стандартного.
 *
 * Смену фильтров серверного дашборда — совместного у владельца, системного у его автора — записывает
 * сервер, поэтому она уходит с задержкой, а до записи панель показывает уже выбранное. Отказ
 * сервера возвращает панель к записанным фильтрам.
 *
 * @param preset - Применённый пользовательский дашборд; `null` — показан стандартный
 * @param standard - Фильтры стандартного дашборда
 */
export const useDashboardPanelFilters = (preset: DashboardPresetItem | null, standard: StandardPanelFilters) => {
  const { activeFilters, setActiveFilters, resetActiveFilters } = useDashboardPresets();

  const [pending, setPending] = useState<PendingFilters | null>(null);
  // Отложенная запись: её доводит до сервера и уход с дашборда раньше срока
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const scheduledWriteRef = useRef<(() => Promise<void>) | null>(null);

  const presetId = preset?.id ?? null;
  const isServerWrite = Boolean((preset?.isShared || preset?.isSystem) && preset.isEditable);

  // Дашборд без своих фильтров показывает умолчание; держим его одним значением, чтобы панель и
  // диаграммы не получали новый период на каждой отрисовке
  const defaults = useMemo(() => getDefaultPanelFilters(), []);

  /** Записывает отложенную смену сейчас; обещание — до ответа сервера. */
  const flushScheduledWrite = useCallback((): Promise<void> => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = null;
    const write = scheduledWriteRef.current;
    scheduledWriteRef.current = null;
    return write ? write() : Promise.resolve();
  }, []);

  // Уход с дашборда — на другой, на стандартный или со страницы — записывает отложенную смену в тот
  // дашборд, где её сделали: запись помнит дашборд, применённый в момент смены
  useEffect(() => {
    // Без применённого дашборда отложенной записи не бывает
    if (!presetId) return;
    return () => {
      void flushScheduledWrite();
    };
  }, [presetId, flushScheduledWrite]);

  const presetFilters = useMemo<DashboardPresetFilters | null>(() => {
    if (!presetId) return null;
    if (pending?.presetId === presetId) return pending.filters;
    return activeFilters ?? defaults;
  }, [presetId, pending, activeFilters, defaults]);

  /**
   * Показывает смену сразу; у серверного дашборда, который пользователь правит, запись `write`
   * уходит с задержкой.
   */
  const commitPresetFilters = useCallback(
    (next: DashboardPresetFilters, write: () => Promise<boolean>) => {
      if (!presetId) return;
      // Новая смена перекрывает ещё не записанную
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = null;
      scheduledWriteRef.current = null;
      if (!isServerWrite) {
        void write();
        return;
      }
      setPending({ presetId, filters: next });
      scheduledWriteRef.current = () =>
        write().then(() => {
          // Записанное — или оставшееся прежним после отказа — показывает уже сам дашборд
          setPending((current) => (current?.filters === next ? null : current));
        });
      timerRef.current = setTimeout(() => void flushScheduledWrite(), SERVER_WRITE_DELAY_MS);
    },
    [presetId, isServerWrite, flushScheduledWrite],
  );

  const filters = useMemo<DashboardFiltersState>(
    () =>
      presetFilters
        ? {
            consultant: presetFilters.consultant,
            dateFrom: presetFilters.dateFrom,
            dateTo: presetFilters.dateTo,
            heatmapMode: standard.filters.heatmapMode,
          }
        : standard.filters,
    [presetFilters, standard.filters],
  );

  const activeAttributeFilters = presetFilters?.attributeFilters ?? standard.activeAttributeFilters;

  /**
   * Фильтры, стоящие в панели: их получает новый дашборд. Консультанта среди них нет — панель
   * пользовательского дашборда его не показывает, и его диаграммы им не отбираются.
   */
  const panelFilters = useMemo<DashboardPresetFilters>(
    () => ({
      consultant: '',
      dateFrom: filters.dateFrom,
      dateTo: filters.dateTo,
      attributeFilters: activeAttributeFilters,
    }),
    [filters.dateFrom, filters.dateTo, activeAttributeFilters],
  );

  const { handleChangeFilters: changeStandardFilters } = standard;

  const handleChangeFilters = useCallback(
    (next: DashboardFiltersState) => {
      if (!presetFilters) {
        changeStandardFilters(next);
        return;
      }
      const { consultant, dateFrom, dateTo } = next;
      if (
        consultant === presetFilters.consultant &&
        dateFrom === presetFilters.dateFrom &&
        dateTo === presetFilters.dateTo
      ) {
        return;
      }
      const nextFilters = { ...presetFilters, consultant, dateFrom, dateTo };
      commitPresetFilters(nextFilters, () => setActiveFilters(nextFilters));
    },
    [presetFilters, changeStandardFilters, commitPresetFilters, setActiveFilters],
  );

  const { handleChangeAttributeFilters: changeStandardAttributeFilters } = standard;

  const handleChangeAttributeFilters = useCallback(
    (next: AttributeFilter[]) => {
      if (!presetFilters) {
        changeStandardAttributeFilters(next);
        return;
      }
      const nextFilters = { ...presetFilters, attributeFilters: next };
      commitPresetFilters(nextFilters, () => setActiveFilters(nextFilters));
    },
    [presetFilters, changeStandardAttributeFilters, commitPresetFilters, setActiveFilters],
  );

  const { handleResetFilters: resetStandardFilters } = standard;

  const handleResetFilters = useCallback(() => {
    if (!presetFilters) {
      resetStandardFilters();
      return;
    }
    const resetFilters = getDefaultPanelFilters();
    commitPresetFilters(resetFilters, () => resetActiveFilters(resetFilters));
    // Сброс — одно нажатие, ждать ему нечего
    void flushScheduledWrite();
  }, [presetFilters, resetStandardFilters, commitPresetFilters, resetActiveFilters, flushScheduledWrite]);

  return {
    filters,
    activeAttributeFilters,
    panelFilters,
    handleChangeFilters,
    handleChangeAttributeFilters,
    handleResetFilters,
    /** Записывает ещё не записанную смену фильтров; обещание — до ответа сервера */
    flushPendingFilters: flushScheduledWrite,
  };
};
